/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/Promise.ts
 * @create:      2025-07-01 13:21:01.665
 * @modify:      2025-07-11 21:19:12.945
 * @version:     6.0.0
 * @times:       10
 * @lines:       93
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Asynchronously pauses execution for a specified number of milliseconds.
 *
 * @param time - The duration to sleep in milliseconds. If `time` is less than or equal to 0, the function resolves immediately.
 * @returns A promise that resolves after the specified time has elapsed.
 * 
 * @since 1.1.7
 * @version 2021-12-11
 */
export async function Sleep(time: number): Promise<void> {
    if (time <= 0) {
        return;
    }

    return await new Promise<void>(resolve => setTimeout(resolve, time));
}

/**
 * Resolves a promise with the provided data after a specified timeout.
 *
 * @param data - The data to be resolved by the promise.
 * @param timeout - The delay in milliseconds before the promise resolves.
 * @returns A promise that resolves with the provided data after the specified timeout.
 * 
 * @since 2.0.6
 * @version 2020-06-02
 */
export async function ResolveAfter<T>(data: T, timeout: number) {
    await Sleep(timeout);
    return Promise.resolve(data);
}

/**
 * Returns a promise that rejects with an error after a specified timeout.
 *
 * @param timeout - The number of milliseconds to wait before rejecting the promise.
 * @returns A promise that rejects with an `Error` indicating the timeout duration.
 *
 * @since 2.0.6
 * @version 2020-06-02
 */
export async function RejectAfter(timeout: number) {
    await Sleep(timeout);
    return Promise.reject(new Error(`timeout: ${timeout}`));
}

/**
 * Returns a promise that resolves or rejects with the result of the given action,
 * or rejects with a timeout error if the action does not settle within the specified time.
 *
 * @param action The promise to race against the timeout.
 * @param timeout The maximum time (in milliseconds) to wait for the action to settle. Defaults to 500ms.
 * @returns A promise that resolves or rejects with the result of the action, or rejects if the timeout is reached first.
 * 
 * @since 2.0.6
 * @version 2020-06-02
 */
export async function TimeoutAfter<T>(action: Promise<T>, timeout = 500) {
    return await Promise.race([action, RejectAfter(timeout)]);
}
